import { useEffect, useMemo, useState, useCallback } from 'react';
import { findParentID } from '../functions/launcherElement/findParentID';
import { updateObjectNestedProperties } from '../functions/launcherElement/updateObjectNestedProperties';
import { recognizeReferencePoint } from '../functions/utils/recognizeReferencePoint';
import { LauncherElementData, useLauncherElementsStore } from './stores/launcherElementsStore';
import { LauncherStatesData, useLauncherStatesStore } from './stores/launcherStatesStore';
import { LauncherWindowData, useLauncherWindowStore } from './stores/launcherWindowStore';
import { useMouseStore } from './stores/mouseStore';
import { UIData, useUIStore } from './stores/uiStore';
import { useScene } from '../context/sceneContext';

export const elementHandleHooks = () => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const [mouseData] = useMouseStore();
  const [launcherElementsData, setLauncherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherStatesData] = useLauncherStatesStore<LauncherStatesData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();


  const zoom = useMemo(() => UIData.sceneZoom, [UIData.sceneZoom]);

  const windowRef = useMemo(() => document.getElementById('launcherWindow'), []);

  const [startDimArr, setStartDimArr] = useState<{ x: number; y: number; width: number; height: number }[]>([]);
  const [handleStartDim, setHandleStartDim] = useState<{ x: number; y: number; width: number; height: number }>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const snappingElementIDs = useMemo(
    () =>
      Object.keys(launcherElementsData || {}).filter(
        (id) => scene.getElementComputedVisibility(id) !== false && !UIData.selectedElementIDs.includes(id)
      ),
    [launcherElementsData, launcherStatesData]
  );

  const getElementHandleDimensions = useCallback(() => {
    const tempRect = {
      windowTop: Number.MAX_VALUE,
      windowLeft: Number.MAX_VALUE,
      top: Number.MAX_VALUE,
      left: Number.MAX_VALUE,
      right: Number.MIN_VALUE,
      bottom: Number.MIN_VALUE,
    };
    
    const windowRef = document.getElementById('launcherWindow');
    const windowRect = windowRef?.getBoundingClientRect();
    
    if (!windowRect) {
      console.warn('Window reference or rect not found');
      return null;
    }

    const visibleElements = UIData.selectedElementIDs.filter(
      id => scene.getElementComputedVisibility(id) !== false
    );

    if (visibleElements.length === 0) {
      console.warn('No visible elements found');
      return null;
    }

    let hasValidElement = false;

    visibleElements.forEach(elementID => {
      const elementDiv = document.getElementById(elementID);
      const elementRect = elementDiv?.getBoundingClientRect();

      if (elementRect) {
        hasValidElement = true;
        tempRect.windowTop = Math.min(tempRect.windowTop, elementRect.top);
        tempRect.windowLeft = Math.min(tempRect.windowLeft, elementRect.left);
        tempRect.top = Math.min(tempRect.top, (elementRect.top - windowRect.top) / zoom);
        tempRect.left = Math.min(tempRect.left, (elementRect.left - windowRect.left) / zoom);
        tempRect.right = Math.max(tempRect.right, (elementRect.right - windowRect.left) / zoom);
        tempRect.bottom = Math.max(tempRect.bottom, (elementRect.bottom - windowRect.top) / zoom);
      } else {
        console.warn(`Element div not found for ID: ${elementID}`);
        // Remove the element from the selected elements
        setUIData({ selectedElementIDs: UIData.selectedElementIDs.filter(id => id !== elementID) });
      }
    });

    if (!hasValidElement) {
      console.warn('No valid element rects found');
      return null;
    }

    return {
      pos: { x: tempRect.left, y: tempRect.top },
      windowPos: { x: tempRect.windowLeft, y: tempRect.windowTop },
      width: tempRect.right - tempRect.left,
      height: tempRect.bottom - tempRect.top,
    };
  }, [UIData.selectedElementIDs, zoom, windowRef]);

  type RectKey = 'left' | 'right' | 'top' | 'bottom';

  const getElementComputedDimensions = useCallback((elementID: string) => {
    const element = launcherElementsData[elementID];
    const data = scene.getElementData(
      elementID,
      {
        left: undefined,
        top: undefined,
        bottom: undefined,
        right: undefined,
      },
      'props.class'
    );
    const referencePoint = recognizeReferencePoint(data);

    let x = element?.props?.class?.[referencePoint.x || 'left'];
    let y = element?.props?.class?.[referencePoint.y || 'top'];
    let width = element?.props?.class?.width;
    let height = element?.props?.class?.height;

    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
      const elementParentID = findParentID(launcherWindowData.children, elementID);
      const parentDiv = document.getElementById(elementParentID || 'launcherWindow');
      const elementDiv = document.getElementById(elementID);
      
      if (parentDiv && elementDiv) {
        const parentRect = parentDiv.getBoundingClientRect();
        const elementRect = elementDiv.getBoundingClientRect();

        // Type-safe access to DOMRect properties
        const elementX = elementRect[referencePoint.x as RectKey] || elementRect.left;
        const parentX = parentRect[referencePoint.x as RectKey] || parentRect.left;
        const elementY = elementRect[referencePoint.y as RectKey] || elementRect.top;
        const parentY = parentRect[referencePoint.y as RectKey] || parentRect.top;

        if (isNaN(x)) x = (elementX - parentX) / zoom;
        if (isNaN(y)) y = (elementY - parentY) / zoom;
        if (isNaN(width)) width = elementRect.width / zoom;
        if (isNaN(height)) height = elementRect.height / zoom;
      }
    }

    return { x, y, width, height };
  }, [launcherElementsData, launcherWindowData.children, zoom]);

  const getAdjustedThreshold = (baseThreshold: number, elementSize: number, zoom: number) => {
    const centerMultiplier = UIData.snapType === 'center' ? 1.5 : 1;
    const sizeAdjustment = Math.min(1, elementSize / 50);
    const zoomAdjustment = 1 / zoom;
    return baseThreshold * sizeAdjustment * zoomAdjustment * centerMultiplier;
  };

  // Snap
  const snapToEdge = (
    startDim: { x: number; y: number; width: number; height: number },
    diff: { x: number; y: number },
    snappingElementIDs: string[]
  ) => {
    const baseThreshold = UIData.snappingGridThreshold;
    const threshold = getAdjustedThreshold(
      baseThreshold,
      startDim.width,
      zoom
    );

    let shift = { x: 0, y: 0 };
    const snappedEdges: {
      elementID?: string;
      elementEdge?: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
      handleEdge: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
      snapType: 'center' | 'edge' | 'distribution';
      isSameLevel: boolean;
    }[] = [];

    // Check if handle is too far from window
    const handlePos = {
      x: startDim.x + diff.x / zoom,
      y: startDim.y + diff.y / zoom
    };

    const maxDistance = launcherWindowData.windowWidth;
    const maxVerticalDistance = launcherWindowData.windowHeight;

    const checkTooFar = (pos: { x: number, y: number, width: number, height: number }) => {
      const horizontallyTooFar = (
        pos.x < -maxDistance ||
        pos.x + pos.width > maxDistance * 2
      );

      const verticallyTooFar = (
        pos.y < -maxVerticalDistance ||
        pos.y + pos.height > maxVerticalDistance * 2
      );

      return horizontallyTooFar || verticallyTooFar;
    };

    // Check current position
    const currentPos = {
      x: handlePos.x,
      y: handlePos.y,
      width: startDim.width,
      height: startDim.height
    };

    if (checkTooFar(currentPos)) {
      return [{ x: 0, y: 0 }, [] as Array<typeof snappedEdges[0]>] as const;
    }

    const windowDiv = document.getElementById('launcherWindow');
    const windowRect = windowDiv?.getBoundingClientRect()!;

    const shiftToSnap = (value: number, handleValue: number) => {
      let shift = null;
      const diff = Math.abs(value - handleValue);

      if (diff <= threshold) {
        shift = value - handleValue;
        if (diff < 1) {
          shift = Math.round(shift * 100) / 100;
        }
      }

      return shift;
    };

    const addSnapEdge = (
      elementID: string,
      elementEdge: typeof snappedEdges[0]['elementEdge'], 
      handleEdge: typeof snappedEdges[0]['handleEdge'], 
      snapType: 'center' | 'edge'
    ) => {
      const selectedElementID = UIData.selectedElementIDs[0];
      const selectedElementParentID = findParentID(launcherWindowData.children, selectedElementID);
      const targetElementParentID = findParentID(launcherWindowData.children, elementID);

      const isSameLevel = 
        selectedElementParentID === targetElementParentID || 
        selectedElementParentID === elementID;

      const isChild = findParentID(launcherWindowData.children, elementID) === selectedElementID;
      if (isChild) return;

      snappedEdges.push({
        elementID,
        elementEdge,
        handleEdge,
        snapType,
        isSameLevel
      });
    };

    const snapToElement = (elementID: string) => {
      const elementDiv = document.getElementById(elementID);
      const elementRect = elementDiv?.getBoundingClientRect()!;

      const elementLauncherWindowRect = {
        x: (elementRect.left - windowRect.left) / zoom,
        y: (elementRect.top - windowRect.top) / zoom,
        width: elementRect.width / zoom,
        height: elementRect.height / zoom,
      };

      const handlePos = {
        x: startDim.x + diff.x / zoom,
        y: startDim.y + diff.y / zoom
      };

      // Add center snapping
      const elementCenterX = elementLauncherWindowRect.x + elementLauncherWindowRect.width / 2;
      const elementCenterY = elementLauncherWindowRect.y + elementLauncherWindowRect.height / 2;
      const handleCenterX = handlePos.x + startDim.width / 2;
      const handleCenterY = handlePos.y + startDim.height / 2;

      // Center X alignment
      const centerXShift = shiftToSnap(elementCenterX, handleCenterX);
      if (centerXShift !== null) {
        if (shift.x === 0 || Math.abs(centerXShift) < Math.abs(shift.x)) {
          shift.x = centerXShift;
          addSnapEdge(elementID, 'centerX', 'centerX', 'center');
        }
      }

      // Center Y alignment
      const centerYShift = shiftToSnap(elementCenterY, handleCenterY);
      if (centerYShift !== null) {
        if (shift.y === 0 || Math.abs(centerYShift) < Math.abs(shift.y)) {
          shift.y = centerYShift;
          addSnapEdge(elementID, 'centerY', 'centerY', 'center');
        }
      }

      // Left edge
      const leftShift = shiftToSnap(elementLauncherWindowRect.x, handlePos.x);
      if (leftShift !== null) {
        if (shift.x === 0 || Math.abs(leftShift) < Math.abs(shift.x)) {
          shift.x = leftShift;
          addSnapEdge(elementID, 'left', 'left', 'edge');
        }
      }

      // Right edge to left
      const rightToLeftShift = shiftToSnap(elementLauncherWindowRect.x + elementLauncherWindowRect.width, handlePos.x);
      if (rightToLeftShift !== null) {
        if (shift.x === 0 || Math.abs(rightToLeftShift) < Math.abs(shift.x)) {
          shift.x = rightToLeftShift;
          addSnapEdge(elementID, 'right', 'left', 'edge');
        }
      }

      // Left edge to right
      const leftToRightShift = shiftToSnap(elementLauncherWindowRect.x, handlePos.x + startDim.width);
      if (leftToRightShift !== null) {
        if (shift.x === 0 || Math.abs(leftToRightShift) < Math.abs(shift.x)) {
          shift.x = leftToRightShift;
          addSnapEdge(elementID, 'left', 'right', 'edge');
        }
      }

      // Right edge
      const rightShift = shiftToSnap(elementLauncherWindowRect.x + elementLauncherWindowRect.width, handlePos.x + startDim.width);
      if (rightShift !== null) {
        if (shift.x === 0 || Math.abs(rightShift) < Math.abs(shift.x)) {
          shift.x = rightShift;
          addSnapEdge(elementID, 'right', 'right', 'edge');
        }
      }

      // Top edge
      const topShift = shiftToSnap(elementLauncherWindowRect.y, handlePos.y);
      if (topShift !== null) {
        if (shift.y === 0 || Math.abs(topShift) < Math.abs(shift.y)) {
          shift.y = topShift;
          addSnapEdge(elementID, 'top', 'top', 'edge');
        }
      }

      // Bottom edge to top
      const bottomToTopShift = shiftToSnap(elementLauncherWindowRect.y + elementLauncherWindowRect.height, handlePos.y);
      if (bottomToTopShift !== null) {
        if (shift.y === 0 || Math.abs(bottomToTopShift) < Math.abs(shift.y)) {
          shift.y = bottomToTopShift;
          addSnapEdge(elementID, 'bottom', 'top', 'edge');
        }
      }

      // Top edge to bottom
      const topToBottomShift = shiftToSnap(elementLauncherWindowRect.y, handlePos.y + startDim.height);
      if (topToBottomShift !== null) {
        if (shift.y === 0 || Math.abs(topToBottomShift) < Math.abs(shift.y)) {
          shift.y = topToBottomShift;
          addSnapEdge(elementID, 'top', 'bottom', 'edge');
        }
      }

      // Bottom edge
      const bottomShift = shiftToSnap(elementLauncherWindowRect.y + elementLauncherWindowRect.height, handlePos.y + startDim.height);
      if (bottomShift !== null) {
        if (shift.y === 0 || Math.abs(bottomShift) < Math.abs(shift.y)) {
          shift.y = bottomShift;
          addSnapEdge(elementID, 'bottom', 'bottom', 'edge');
        }
      }
    };

    // Check window center snapping
    const handleCenterX = startDim.x + diff.x / zoom + startDim.width / 2;
    const handleCenterY = startDim.y + diff.y / zoom + startDim.height / 2;
    const windowCenterX = launcherWindowData.windowWidth / 2;
    const windowCenterY = launcherWindowData.windowHeight / 2;

    // Center X alignment with window
    const windowCenterXShift = shiftToSnap(windowCenterX, handleCenterX);
    if (windowCenterXShift !== null) {
      if (shift.x === 0 || Math.abs(windowCenterXShift) < Math.abs(shift.x)) {
        shift.x = windowCenterXShift;
        snappedEdges.push({ 
          handleEdge: 'centerX', 
          snapType: 'center',
          isSameLevel: true
        });
      }
    }

    // Center Y alignment with window
    const windowCenterYShift = shiftToSnap(windowCenterY, handleCenterY);
    if (windowCenterYShift !== null) {
      if (shift.y === 0 || Math.abs(windowCenterYShift) < Math.abs(shift.y)) {
        shift.y = windowCenterYShift;
        snappedEdges.push({ 
          handleEdge: 'centerY', 
          snapType: 'center',
          isSameLevel: true
        });
      }
    }

    snappingElementIDs.forEach(snapToElement);

    // Handle window edges only for edge snapping
    if (UIData.snapType === 'edge') {
      // Remove window boundary constraints
      const handlePos = {
        x: startDim.x + diff.x / zoom,
        y: startDim.y + diff.y / zoom
      };

      // Only add snapping edges when near window edges
      if (Math.abs(handlePos.x) < threshold) {
        shift.x = -handlePos.x;
        snappedEdges.push({ handleEdge: 'left', snapType: 'edge', isSameLevel: true });
      }
      if (Math.abs(handlePos.x + startDim.width - launcherWindowData.windowWidth) < threshold) {
        shift.x = launcherWindowData.windowWidth - (handlePos.x + startDim.width);
        snappedEdges.push({ handleEdge: 'right', snapType: 'edge', isSameLevel: true });
      }
      if (Math.abs(handlePos.y) < threshold) {
        shift.y = -handlePos.y;
        snappedEdges.push({ handleEdge: 'top', snapType: 'edge', isSameLevel: true });
      }
      if (Math.abs(handlePos.y + startDim.height - launcherWindowData.windowHeight) < threshold) {
        shift.y = launcherWindowData.windowHeight - (handlePos.y + startDim.height);
        snappedEdges.push({ handleEdge: 'bottom', snapType: 'edge', isSameLevel: true });
      }
    }

    // After calculating shifts, check if the final position would be too far
    const finalPos = {
      x: handlePos.x + shift.x,
      y: handlePos.y + shift.y,
      width: startDim.width,
      height: startDim.height
    };

    // If final position is too far, only keep snaps that are within bounds
    if (checkTooFar(finalPos)) {
      // Clear all snaps and shifts
      shift.x = 0;
      shift.y = 0;
      snappedEdges.length = 0;
    }

    return [shift, snappedEdges] as const;
  };

  const snapToGrid = (
    startDim: { x: number; y: number; width: number; height: number },
    diff: { x: number; y: number }
  ) => {
    const snappingThreshold = UIData.snappingGridThreshold;
    const gridSize = UIData.snappingGridSize;
    let x = 0;
    let y = 0;
    const width = startDim.width;
    const height = startDim.height;
    const snappedEdges: {
      handleEdge: 'left' | 'top' | 'bottom' | 'right';
      snapType: 'edge' | 'center' | 'distribution';
      isSameLevel: boolean;
    }[] = [];

    const shiftToSnap = (value: number, size?: number) => {
      let shift = null;
      const isInThresholdBefore = (value: number) => value % gridSize <= snappingThreshold;
      const isInThresholdAfter = (value: number) => value % gridSize >= gridSize - snappingThreshold;

      if (size) {
        // before
        if (isInThresholdAfter(value + size)) {
          shift = Math.ceil((value + size) / gridSize) * gridSize - (value + size);
        }
        // after
        if (isInThresholdBefore(value + size)) {
          shift = Math.floor((value + size) / gridSize) * gridSize - (value + size);
        }
      } else {
        if (isInThresholdBefore(value)) {
          shift = Math.floor(value / gridSize) * gridSize - value;
        }
        if (isInThresholdAfter(value)) {
          shift = Math.ceil(value / gridSize) * gridSize - value;
        }
      }

      return shift;
    };

    const leftSnapShift = shiftToSnap(Math.round(startDim.x + diff.x / zoom));
    const rightSnapShift = shiftToSnap(Math.round(startDim.x + diff.x / zoom), width);
    const topSnapShift = shiftToSnap(Math.round(startDim.y + diff.y / zoom));
    const bottomSnapShift = shiftToSnap(Math.round(startDim.y + diff.y / zoom), height);

    const shift = {
      x: leftSnapShift || rightSnapShift || 0,
      y: topSnapShift || bottomSnapShift || 0,
    };

    if (
      startDim.x + diff.x / zoom < 0 ||
      startDim.x + diff.x / zoom + width > launcherWindowData.windowWidth ||
      startDim.y + diff.y / zoom < 0 ||
      startDim.y + diff.y / zoom + height > launcherWindowData.windowHeight
    ) {
      shift.x = 0;
      shift.y = 0;
    } else {
      if (leftSnapShift !== null) snappedEdges.push({ handleEdge: 'left', snapType: 'edge', isSameLevel: true });
      else if (rightSnapShift !== null) snappedEdges.push({ handleEdge: 'right', snapType: 'edge', isSameLevel: true });
      if (topSnapShift !== null) snappedEdges.push({ handleEdge: 'top', snapType: 'edge', isSameLevel: true });
      else if (bottomSnapShift !== null) snappedEdges.push({ handleEdge: 'bottom', snapType: 'edge', isSameLevel: true });
    }

    return [shift, snappedEdges] as const;
  };

  // Move calculateElementUpdate before updateElements
  const calculateElementUpdate = useCallback((
    elementID: string,
    startDim: { x: number; y: number; width: number; height: number },
    diff: { x: number; y: number },
    snappingShift: { x: number; y: number }
  ) => {
    let updatedElement = launcherElementsData[elementID];

    const data = scene.getElementData(
      elementID,
      {
        left: undefined,
        top: undefined,
        bottom: undefined,
        right: undefined,
      },
      'props.class'
    );
    const referencePoint = recognizeReferencePoint(data);

    const newX = Math.round(startDim.x + diff.x / zoom);
    const newY = Math.round(startDim.y + diff.y / zoom);
    const newWidth = UIData.elementHandle.isResizingX
      ? Math.round(startDim.width + (diff.x / zoom) * (UIData.elementHandle.isResizingReversedX ? -1 : 1))
      : startDim.width;
    const newHeight = UIData.elementHandle.isResizingY
      ? Math.round(startDim.height + (diff.y / zoom) * (UIData.elementHandle.isResizingReversedY ? -1 : 1))
      : startDim.height;

    const snappedPos = { x: Math.round(newX + snappingShift.x), y: Math.round(newY + snappingShift.y) };
    const snappedSize = { width: newWidth + snappingShift.x, height: newHeight + snappingShift.y };

    if (referencePoint.x === 'right') {
      if (UIData.elementHandle.isDraggingX && UIData.elementHandle.isResizingX) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.width': snappedSize.width,
        });
      } else {
        if (UIData.elementHandle.isDraggingX) {
          updatedElement = updateObjectNestedProperties(updatedElement, {
            'props.class.position': 'absolute',
            'props.class.right': -snappedPos.x,
          });
        }
        if (UIData.elementHandle.isResizingX) {
          updatedElement = updateObjectNestedProperties(updatedElement, {
            'props.class.position': 'absolute',
            'props.class.width': snappedSize.width,
            'props.class.right': -snappedPos.x,
          });
        }
      }
    } else {
      if (UIData.elementHandle.isDraggingX) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.position': 'absolute',
          'props.class.left': snappedPos.x,
        });
      }
      if (UIData.elementHandle.isResizingX) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.width': snappedSize.width,
        });
      }
    }

    if (referencePoint.y === 'bottom') {
      if (UIData.elementHandle.isDraggingY && UIData.elementHandle.isResizingY) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.height': snappedSize.height,
        });
      } else {
        if (UIData.elementHandle.isDraggingY) {
          updatedElement = updateObjectNestedProperties(updatedElement, {
            'props.class.position': 'absolute',
            'props.class.bottom': -snappedPos.y,
          });
        }
        if (UIData.elementHandle.isResizingY) {
          updatedElement = updateObjectNestedProperties(updatedElement, {
            'props.class.height': snappedSize.height,
            'props.class.bottom': -snappedPos.y,
          });
        }
      }
    } else {
      if (UIData.elementHandle.isDraggingY) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.position': 'absolute',
          'props.class.top': snappedPos.y,
        });
      }
      if (UIData.elementHandle.isResizingY) {
        updatedElement = updateObjectNestedProperties(updatedElement, {
          'props.class.height': snappedSize.height,
        });
      }
    }

    return updatedElement;
  }, [launcherElementsData, zoom, UIData.elementHandle]);

  // Batch updates for element positions
  const updateElements = useCallback((
    elementIDs: string[],
    startDims: Array<{ x: number; y: number; width: number; height: number }>,
    diff: { x: number; y: number },
    snappingShift: { x: number; y: number }
  ) => {
    const updates: Record<string, any> = {};
    
    elementIDs.forEach((elementID, index) => {
      const startDim = startDims[index];
      const updatedElement = calculateElementUpdate(elementID, startDim, diff, snappingShift);
      updates[elementID] = updatedElement;
    });

    setLauncherElementsData(updates);
  }, [calculateElementUpdate]);

  // ---------------------------------------------------

  // Show handle, set handle dimensions
  useEffect(() => {
    const visibleElements = UIData.selectedElementIDs.filter(
      (id) => scene.getElementComputedVisibility(id) !== false
    );

    if (visibleElements.length > 0) {
      const dim = getElementHandleDimensions();
      
      if (dim && dim.pos && typeof dim.width === 'number' && typeof dim.height === 'number') {
        // Add validation for dimensions
        if (isNaN(dim.pos.x) || isNaN(dim.pos.y) || 
            isNaN(dim.width) || isNaN(dim.height) ||
            dim.width <= 0 || dim.height <= 0) {
          console.warn('Invalid dimensions:', dim);
          setUIData({ elementHandle: { ...UIData.elementHandle, isVisible: false } });
          return;
        }

        setUIData({
          elementHandle: {
            ...UIData.elementHandle,
            isVisible: true,
            pos: { x: dim.pos.x || 0, y: dim.pos.y || 0 },
            width: dim.width,
            height: dim.height,
          },
        });
      } else {
        console.warn('Failed to get element handle dimensions');
        setUIData({ elementHandle: { ...UIData.elementHandle, isVisible: false } });
      }
    } else {
      setUIData({ elementHandle: { ...UIData.elementHandle, isVisible: false } });
    }
  }, [UIData.selectedElementIDs, launcherElementsData, UIData.openPopupIDs, launcherStatesData]);

  useEffect(() => {
    if (mouseData.isDown && mouseData.button === 0) {
      // Instant drag
      if (!mouseData.hoveredLauncherElementType) return;
      setUIData({
        elementHandle: {
          ...UIData.elementHandle,
          isDragging: true,
          isResizing: false,
          isDraggingX: true,
          isDraggingY: true,
          isResizingX: false,
          isResizingY: false,
          isResizingReversedX: false,
          isResizingReversedY: false,
        },
      });
    } else {
      // Reset
      setStartDimArr([]);
      setUIData({
        elementHandle: {
          ...UIData.elementHandle,
          isDragging: false,
          isResizing: false,
          isDraggingX: false,
          isDraggingY: false,
          isResizingX: false,
          isResizingY: false,
          isResizingReversedX: false,
          isResizingReversedY: false,
        },
      });
      setUIData({ snappedEdges: [] });
    }
  }, [mouseData.isDown]);

  // Init
  useEffect(() => {
    if (UIData.selectedElementIDs.length === 0 || !mouseData.isDragging) return;
    if (mouseData.hoveredUIElement === 'elementTreeNode') return;
    if (mouseData.button !== 0) return;
    
    // Add null checks for elementHandle position
    if (!UIData.elementHandle.pos) {
      console.warn('Element handle position is undefined');
      return;
    }

    setHandleStartDim({
      x: UIData.elementHandle.pos.x || 0,
      y: UIData.elementHandle.pos.y || 0,
      width: UIData.elementHandle.width || 0,
      height: UIData.elementHandle.height || 0,
    });
    setStartDimArr(
      UIData.selectedElementIDs.map((selectedElementID) => {
        const computedDimensions = getElementComputedDimensions(selectedElementID);

        const data = scene.getElementData(
          selectedElementID,
          {
            left: undefined,
            top: undefined,
            bottom: undefined,
            right: undefined,
          },
          'props.class'
        );
        const referencePoint = recognizeReferencePoint(data);

        return {
          x: referencePoint.x === 'right' ? -computedDimensions.x : computedDimensions.x,
          y: referencePoint.y === 'bottom' ? -computedDimensions.y : computedDimensions.y,
          width: computedDimensions.width,
          height: computedDimensions.height,
        };
      })
    );
  }, [mouseData.isDragging]);

  // Update
  useEffect(() => {
    if (UIData.selectedElementIDs.length === 0 || startDimArr.length === 0) return;
    const diff = mouseData.draggingDiff;
    if (!diff) return;
    if (!UIData.elementHandle.isDragging && !UIData.elementHandle.isResizing) return;

    // Add validation for handleStartDim
    if (!handleStartDim || typeof handleStartDim.x !== 'number' || typeof handleStartDim.y !== 'number') {
      console.warn('Invalid handle start dimensions');
      return;
    }

    let snappingShift = { x: 0, y: 0 };
    
    if (UIData.snapToEdge && !UIData.elementHandle.isResizing) {
      const [shift, snappedEdges] = snapToEdge(handleStartDim, diff, snappingElementIDs);
      snappingShift = shift;
      setUIData({ snappedEdges });
    } else if (UIData.snapToGrid && !UIData.elementHandle.isResizing) {
      const [shift, snappedEdges] = snapToGrid(handleStartDim, diff);
      snappingShift = shift;
      setUIData({ snappedEdges });
    }

    updateElements(UIData.selectedElementIDs, startDimArr, diff, snappingShift);
  }, [mouseData.draggingDiff, UIData.elementHandle.isDragging, UIData.elementHandle.isResizing]);
};
